<template>
  <div class="canvas-grid-container" :style="canvasGridContainerStyle">
    <!-- Fixed spacebreak column on left hand side -->
    <SpacebreakSidebar
      v-if="selectedView === views.Tile"
      :points-of-distribution="pointsOfDistribution"
    />
    <template v-if="!isDisplayByCDT && !unifiedColumns[selectedView]">
      <CanvasColumn
        v-for="attribute in availableProductAttributesValues(displayBy)"
        :key="attribute.id"
        :name="attribute.name"
        :products="productsSplittedByAttribute[attribute.id]"
      />
      <CanvasColumn
        v-if="hasProductsWithEmptyAttribute"
        :name="productsWithEmptyAttributeTitle"
        :products="productsWithEmptyAttribute"
      />
    </template>
    <template v-else-if="!unifiedColumns[selectedView]">
      <!-- Expandable column per cann group -->
      <CannGroupColumn
        v-for="scenarioCdt in orderedScenarioCdts"
        :key="scenarioCdt._id"
        :scenario-cdt="scenarioCdt"
      />
    </template>
    <template v-else>
      <CanvasColumn
        :name="$t('assortmentCanvasPage.allCannGroups')"
        :products="sortedUnifiedProducts"
      />
    </template>

    <ProductPanel />
  </div>
</template>

<script>
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import assortmentCanvases from '@enums/assortment-canvases';
import SpacebreakTypes from '@enums/spacebreak-types';
import { cloneDeep, get, groupBy, isEmpty, size } from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  name: 'CanvasGrid',
  props: {
    orderedScenarioCdts: {
      required: true,
      type: Array,
    },
  },

  data() {
    return {
      views: assortmentCanvases.Views,
      isSidePanelOpen: false,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'spacebreaks',
      'unifiedColumns',
      'selectedView',
      'stickyHeaders',
      'selectedPod',
      'totalExpandedHeaderDepth',
      'canvasProducts',
      'getFilteredProducts',
      'getHighlightedProducts',
      'sortByProductsInCanvas',
      'displayBy',
      'availableProductAttributesValues',
    ]),
    ...mapState(useContextStore, ['getClientConfig']),

    hasHighlightProductEnabled() {
      return get(this.getClientConfig, 'features.highlightProductEnabled', false);
    },

    canvasGridContainerStyle() {
      const defaultClass = { 'grid-template-rows': `repeat(${this.numberOfRows}, max-content)` };
      if (this.unifiedColumns[this.selectedView])
        return {
          ...defaultClass,
          'grid-template-columns': this.selectedView === this.views.Tile ? 'auto 1fr' : '1fr',
        };
      return {
        ...defaultClass,
        'grid-auto-columns': 'max-content',
      };
    },

    pointsOfDistribution() {
      // Points of distribution are calculated across all storeclasses so we select the one for this canvas.
      return get(this, 'selectedPod', {});
    },

    numberOfRows() {
      // Account for the additional spacebreak bays at the bottom of the canvas grid
      const additionalSpacebreaks = size(SpacebreakTypes.spacebreakTypes);
      // Account for the Cann Group Name header at the very top of the grid
      const cannGroupHeaderCount = 1;
      // totalExpandedHeaderDepth works out the maximum depth of the expanded headers so we can work out how many extra rows there are in the headers
      // We times by 2 as each time we expand a header it has 2 new rows.
      // Return the number of rows in the entire canvas grid, we set this is the template above to tell CSS Grid how many times to repeat the pattern.
      const newRowCount =
        this.spacebreaks.length +
        this.totalExpandedHeaderDepth * 2 +
        cannGroupHeaderCount +
        additionalSpacebreaks;

      this.setNumberOfRowsInGrid(newRowCount);
      return newRowCount;
    },

    sortedUnifiedProducts() {
      const productsInView = this.hasHighlightProductEnabled
        ? this.getHighlightedProducts
        : this.getFilteredProducts;
      const products = cloneDeep(productsInView.products);
      // mark the products to be highlighted
      if (this.hasHighlightProductEnabled) {
        products.forEach(p => {
          p.isHighlighted = productsInView.highlightedProductsSet.has(p.productKey);
        });
      }
      return products.sort(this.sortByProductsInCanvas);
    },

    productsSplittedByAttribute() {
      return groupBy(this.sortedUnifiedProducts, this.displayBy);
    },

    isDisplayByCDT() {
      return this.displayBy === assortmentCanvases.DisplayBy.cdt;
    },

    productsWithEmptyAttribute() {
      return this.sortedUnifiedProducts.filter(product => !product[this.displayBy]);
    },

    hasProductsWithEmptyAttribute() {
      return !isEmpty(this.productsWithEmptyAttribute);
    },

    productsWithEmptyAttributeTitle() {
      const attributeNameTranslation = this.$t(`assortmentCanvasPage.displayBy.${this.displayBy}`);
      return `${this.$t('general.no')} ${attributeNameTranslation}`;
    },
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, ['setNumberOfRowsInGrid']),

    openProductSidebar() {
      this.isSidePanelOpen = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.column-title {
  font-size: 2rem;
}

.side-panel-drawer {
  min-width: fit-content;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
